import { PAGES, QUERY_PARAM_KEYS, QUERY_PARAM_VALUES } from '$lib/utils/constants.js';
import { openModal } from 'svelte-modals';
import ModalDNSMI from '$lib/components/modals/ModalDNSMI.svelte';

const ctaButton = {
	text: 'Try FREE for 30 Days!',
	href: PAGES.INTERNAL.SUBSCRIPTION,
	disclaimer: 'Then just $14.99/mo. until canceled',
};

/**
 * @type {import('./index.js').HomePageConfiguration}
 */
const config = {
	freeToPlay: true,
	hero: {
		bannerImg: {
			sources: [{ srcset: '/abc/src/lib/assets/images/home/NewHeroBanner-NMHP.svg' }],
			alt: "Your child's path to success starts here",
		},
		headline:
			'Welcome to the next evolution of ABCmouse– <br /> completely reimagined to deliver a richer, <br /> more personalized learning experience <br /> for children ages 2–8.',
		titleText: {
			superTitle: "YOUR CHILD'S",
			title: 'Path to Success',
			subtitle: 'STARTS HERE',
			description: 'Learn anywhere. Cancel anytime.',
		},
		ctaButton: ctaButton,
		badgeHref: `${PAGES.INTERNAL.SUBSCRIPTION}?${QUERY_PARAM_KEYS.PRODUCT_TYPE}=${QUERY_PARAM_VALUES.B_TERM.ANNUAL}`,
		badgeImg: {
			sources: [{ srcset: '/assets-mkt/routes/home/clementine-annual-badge-v3.svg' }],
			alt: 'Annual Offer 74% Off',
		},
		abcHero: {
			sources: [{ srcset: '/assets-mkt/routes/home/abcmouse-portal-hero-v4.png' }],
			alt: 'ABCmouse Character',
		},
		TeacherHero: {
			sources: [{ srcset: '/assets-mkt/routes/home/teacher-portal-hero-v2.png' }],
			alt: 'Teacher Character',
		},
		MelodyHero: {
			sources: [{ srcset: '/assets-mkt/routes/home/melody-portal-hero-v2.png' }],
			alt: 'Melody Character',
		},
		subjectsBanner: {
			subjects: ['Reading', 'Math', 'Science', 'Art & Colors', 'Music'],
		},
	},
	video: {
		bannerImg: {
			sources: [{ srcset: '/assets-mkt/routes/home/15_years_v2.svg' }],
			alt: 'Educational Excellence Banner',
		},
		headline: 'Educational Excellence',
		subheadline:
			'Now modernized to better support the next <br /> generation of explorers, thinkers, and creators.',
		videoSrc: '/assets-mkt/routes/home/nmhp_2_0_video_v4.mp4',
		videoPoster: '/assets-mkt/routes/home/nmhp_2_0_video_thumbnail_v1.png',
		overlayImg: {
			sources: [{ srcset: '/assets-mkt/routes/home/abc_popcorn_v3.png' }],
			alt: 'ABCmouse with popcorn',
		},
	},
	valueProps: {
		bgImg: {
			base: '/assets-mkt/routes/home/white_squiggle_down_v2.svg',
		},
		items: [
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/prop_ice_game_v3.png' }],
					alt: 'Research-Backed Curriculum',
				},
				headline: 'Research-Backed <br /> Curriculum — Expanded!',
				body: 'With research studies informed by more than 15 years of experience, our learning experts have elevated the ABCmouse curriculum to help learners of every level achieve success.',
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/prop_design_v2.png' }],
					alt: 'Vibrant New Designs',
				},
				headline: 'Vibrant New Designs That <br /> Captivate Young Learners',
				body: "Every experience has been thoughtfully reimagined to create a world that's more immersive, interactive, and designed to delight your child in every way.",
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/prop_reading_math_v2.png' }],
					alt: 'Custom Learning Paths',
				},
				headline: 'Custom Learning Paths <br /> in Reading and Math',
				body: 'Educator-designed paths in Reading and Math make learning feel like an adventure, with purposeful guidance to ensure children are always moving on to the appropriate next step.',
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/prop_bot_beat_v2.png' }],
					alt: 'All-New Worlds',
				},
				headline: 'All-New Worlds <br /> to Learn and Explore',
				body: 'From Pet Town and Bot Beats to the Animal Safari and Hamster Habitat, every tap invites discovery and growth <br />—because playing is learning.',
			},
		],
	},
	proofCTA: {
		bannerImg: {
			sources: [{ srcset: '/assets-mkt/routes/home/proven_results_banner_v2.svg' }],
			alt: 'Proven Results Banner',
		},
		headline:
			'ABCmouse <br /> <span class="text-sunshine-yellow">DOUBLES Early Learning Gains</span> <br /> in Reading and Math',
		subheadline: 'Backed by Research. <br />Trusted by 45M+ Families.',
		ctaButton: ctaButton,
		disclaimer:
			'Age of Learning study; PreK kids; min. 2 day/wk & 1 hr/wk ABCmouse Premium usage. See aofl.com/research for more info.',
		bgImg: {
			base: '/assets-mkt/routes/home/proof_bg_sm_v6.png',
			sm: '/assets-mkt/routes/home/proof_bg_md_v6.png',
			md: '/assets-mkt/routes/home/proof_bg_lg_v6.png',
			lg: '/assets-mkt/routes/home/proof_bg_xl_v5.png',
			xl: '/assets-mkt/routes/home/proof_bg_2xl_v5.png',
		},
	},
	subjects: {
		headline: 'A Comprehensive Curriculum <br /> for Fully-Rounded Success',
		subheadline: 'All Subjects – All Learners – Every Skill',
		arrowLeftImg: {
			sources: [{ srcset: '/assets-mkt/routes/home/clementine-arrow_left_v1.svg' }],
			alt: 'Previous',
		},
		arrowRightImg: {
			sources: [{ srcset: '/assets-mkt/routes/home/clementine-arrow_right_v1.svg' }],
			alt: 'Next',
		},
		items: [
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/subject_reading_v2.png' }],
					alt: 'Reading and Language Arts',
				},
				title: 'Reading and Language Arts',
				description:
					'From phonics to storytelling, children develop literacy skills through engaging stories, interactive books, and creative writing activities.',
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/subject_math_v2.png' }],
					alt: 'Mathematics',
				},
				title: 'Mathematics',
				description:
					'Numbers come alive through visual puzzles, hands-on counting games, and problem-solving adventures that make math feel like play.',
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/subject_science_v2.png' }],
					alt: 'Science and Discovery',
				},
				title: 'Science and Discovery',
				description:
					'Curious minds explore the natural world through virtual experiments, nature documentaries, and interactive simulations.',
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/subject_art_v2.png' }],
					alt: 'Creative Art and Music',
				},
				title: 'Creative Art and Music',
				description:
					'Self-expression flourishes through digital art studios, music composition tools, and creative projects that celebrate imagination.',
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/subject_growth_v2.png' }],
					alt: 'Helping Kids Grow',
				},
				title: 'Helping Kids Grow',
				description:
					'Character-driven narratives and immersive scenarios help children develop emotional awareness, self-regulation, and social confidence.',
			},
		],
	},
	rewards: {
		headline: 'Tickets and Rewards System',
		subheadline: 'Learning Celebrated Every Step of the Way',
		image: {
			sources: [{ srcset: '/assets-mkt/routes/home/rewards_v2.png' }],
			alt: 'Rewards Main',
		},
		body: 'ABCmouse rewards progress with tickets that kids use to expand their experiences in Pet Town, Aquarium, and Hamster Maze — supporting creative play, responsibility, and problem-solving skills along the way.',
		ctaButton: ctaButton,
	},
	testimonials: {
		topBgImg: {
			base: '/assets-mkt/routes/home/white_squiggle_down_v2.svg',
		},
		bottomBgImg: {
			base: '/assets-mkt/routes/home/white_squiggle_up_v2.svg',
		},
		bannerImg: {
			sources: [{ srcset: '/assets-mkt/routes/home/real_families_real_results_v2.svg' }],
			alt: 'Testimonials Banner',
		},
		quoteImg: {
			sources: [{ srcset: '/assets-mkt/routes/home/clementine_quotes_v1.svg' }],
			alt: 'Quotation Mark',
		},
		leftArrowImg: {
			sources: [{ srcset: '/assets-mkt/routes/home/clementine-arrow_left_v1.svg' }],
			alt: 'Previous',
		},
		rightArrowImg: {
			sources: [{ srcset: '/assets-mkt/routes/home/clementine-arrow_right_v1.svg' }],
			alt: 'Next',
		},
		items: [
			{
				quote: `“I think it's incredibly important at this young age for them to have that foundation of learning is fun.”`,
				author: 'Andrew,',
				subtext: 'Dad of 10-, 9-, and 5-year-old boys',
			},
			{
				quote:
					'“Math used to be really hard for me, but now that I have ABCmouse, it’s pretty easy.”',
				author: 'Ava, Age 6',
				subtext: '',
			},
			{
				quote: '“It feels like we found a parenting hack. <br />It’s guilt-free.”',
				author: 'Adriana,',
				subtext: 'Mom of three children',
			},
		],
	},
	premiumValueProps: {
		items: [
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/library_v3.png' }],
					alt: 'Premium Feature 1',
				},
				text: 'Unlimited access to our complete library of over 13,000 activities',
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/child_profiles_v3.png' }],
					alt: 'Premium Feature 2',
				},
				text: 'Up to three child profiles',
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/bot_beat_music_v3.png' }],
					alt: 'Premium Feature 3',
				},
				text: 'All curriculum subjects including Science, Coding, Art & Music, and more',
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/content_v3.png' }],
					alt: 'Premium Feature 4',
				},
				text: 'New content added <br /> all the time',
			},
		],
		ctaButton: ctaButton,
	},
	joinCTA: {
		topBgImg: {
			base: '/assets-mkt/routes/home/white_squiggle_down_v2.svg',
		},
		bottomBgImg: {
			base: '/assets-mkt/routes/home/white_squiggle_up_v2.svg',
		},
		bottomBlueBgImg: {
			base: '/assets-mkt/routes/home/blue_squiggle_v2.svg',
		},
		heroImg: {
			sources: [{ srcset: '/assets-mkt/routes/home/theater_characters_v2.png' }],
			alt: 'Theater Characters',
		},
		headline: "Your Child's Learning Success Starts Here",
		subheadline:
			'Join the millions of families who trust ABCmouse to prepare their children for school—and beyond!',
		ctaButton: ctaButton,
	},
	footer: {
		logoImg: {
			sources: [{ srcset: '/assets-mkt/routes/home/abcmouse-logo-rebrand-main.svg' }],
			alt: 'ABCmouse logo',
		},
		socials: [
			{
				name: 'TikTok',
				icon: {
					sources: [{ srcset: '/assets-mkt/routes/home/social_tiktok_v1.svg' }],
					alt: 'TikTok',
				},
				url: PAGES.EXTERNAL.TIKTOK,
			},
			{
				name: 'Facebook',
				icon: {
					sources: [{ srcset: '/assets-mkt/routes/home/social_facebook_v1.svg' }],
					alt: 'Facebook',
				},
				url: PAGES.EXTERNAL.FACEBOOK,
			},
			{
				name: 'Instagram',
				icon: {
					sources: [{ srcset: '/assets-mkt/routes/home/social_instagram_v1.svg' }],
					alt: 'Instagram',
				},
				url: PAGES.EXTERNAL.INSTAGRAM,
			},
			{
				name: 'YouTube',
				icon: {
					sources: [{ srcset: '/assets-mkt/routes/home/social_youtube_v1.svg' }],
					alt: 'YouTube',
				},
				url: PAGES.EXTERNAL.YOUTUBE,
			},
		],
		popularResourcesLinks: [
			{
				id: 'about-us',
				text: 'About Us',
				href: PAGES.EXTERNAL.ABOUT_US,
				aria: 'Learn about Age of Learning and all our Award-Winning Products.',
				target: '_blank',
			},
			{
				id: 'press',
				trackId: 'footer:press',
				text: 'Press',
				href: PAGES.EXTERNAL.PRESS,
				aria: 'Press',
				target: '_blank',
			},
			{
				id: 'libraries',
				trackId: 'footer:libraries',
				text: 'Libraries',
				href: PAGES.EXTERNAL.LIBRARIES,
				aria: 'Libraries',
				target: '_blank',
			},
			{
				id: 'shop',
				trackId: 'footer:shop',
				text: 'Shop',
				href: PAGES.EXTERNAL.SHOP,
				aria: 'Shop',
				target: '_blank',
			},
			{
				id: 'careers',
				trackId: 'footer:careers',
				text: 'Careers',
				href: PAGES.EXTERNAL.CAREERS,
				aria: 'Careers',
				target: '_blank',
			},
		],
		moreAbcmouseLinks: [
			{
				id: 'teachers',
				text: 'Teachers',
				href: PAGES.INTERNAL.LEGACY.TEACHERS,
				aria: 'ABCmouse for Teachers',
				target: '_blank',
			},
			{
				id: 'learning',
				text: 'Learning',
				href: PAGES.EXTERNAL.LEARN,
				aria: 'ABCmouse Learning',
				target: '_blank',
			},
			{
				id: 'games',
				text: 'Games',
				href: PAGES.EXTERNAL.ABCMOUSE_SEO_GAMES_PAGE,
				aria: 'ABCmouse Games',
				target: '_blank',
			},
			{
				id: 'activities',
				text: 'Activities',
				href: PAGES.EXTERNAL.ACTIVITIES,
				aria: 'ABCmouse Activities',
				target: '_blank',
			},
			{
				id: 'printables',
				text: 'Printables',
				href: PAGES.EXTERNAL.PRINTABLES,
				aria: 'ABCmouse Printables',
				target: '_blank',
			},
		],
		links: [
			{
				id: 'trade-mark',
				trackId: 'footer:copyright',
				text: `TM & © 2007-${new Date().getFullYear()} Age of Learning, Inc. All rights reserved.<br />`,
				href: PAGES.EXTERNAL.AOFL,
				aria: 'Age of Learning Inc. Copyright Notice',
				target: '_blank',
				rel: 'noopener noreferrer',
			},
			{
				id: 'terms-conditions',
				trackId: 'footer:terms-and-conditions',
				text: 'Terms & Conditions',
				href: PAGES.EXTERNAL.NMHP_TERMS_CONDITIONS,
				aria: 'Terms and Conditions',
				target: '_blank',
				rel: 'noopener noreferrer',
			},
			{
				id: 'privacy-policy',
				trackId: 'footer:privacy-policy',
				text: 'Privacy Policy',
				href: PAGES.EXTERNAL.NMHP_PRIVACY_POLICY,
				aria: 'Privacy Policy',
				target: '_blank',
				rel: 'noopener noreferrer',
			},
			{
				id: 'do-not-sell',
				trackId: 'footer:do-not-sell-my-info',
				text: 'Do Not Sell My Personal Information',
				href: '#do-not-sell',
				aria: 'Do Not Sell My Personal Information',
				type: 'callback',
				callback: () =>
					openModal(ModalDNSMI, {
						modalImageSrc: '/assets-mkt/template/cookie-consent-modal/abc_logo-v1.png',
					}),
			},
			{
				id: 'accessibility',
				text: 'Accessibility&nbsp;Statement',
				href: PAGES.EXTERNAL.ACCESSIBILITY,
				aria: 'Accessibility Statement',
				target: '_blank',
				rel: 'noopener noreferrer',
			},
		],
		address: [
			{
				id: 'address',
				trackId: 'footer:address',
				text: 'ABCmouse 101 N. Brand Blvd., 7th Floor, Glendale, CA 91203',
				href: PAGES.EXTERNAL.AOFL_ADDRESS,
				aria: 'ABCmouse 101 N. Brand Blvd., 7th Floor, Glendale, CA 91203',
				target: '_blank',
				rel: 'noopener noreferrer',
			},
		],
	},
};

export default config;
